import { BAY_PIN_LABEL } from '@butter/library/simulatorCompanion'
import type { AccessDoor } from './types'

/**
 * Access-surface copy, in one place.
 *
 * Pure and string-returning so both the mobile page and the web unlock page phrase a
 * multi-door club identically. Every sentence here was previously inlined in the mobile page
 * and would have been re-inlined, slightly differently, by the second surface.
 */

/** Heading naming what a single unlock opens. */
export function doorTitle(doors: AccessDoor[]): string {
  if (doors.length === 0) return DEFAULT_DOOR_NAME
  if (doors.length === 1) return doors[0].name
  return `${doors.length} Doors`
}

/** "door" / "doors", for sentences that need to agree with the set size. */
export function doorNoun(doors: AccessDoor[]): string {
  return doors.length > 1 ? 'doors' : 'door'
}

/**
 * Join door names into a readable list: "Front Door and Range Gate",
 * "Front Door, Range Gate and Bay 3". Mirrors `formatDoorNames` in the unlock controller so
 * the page and the server's result message read the same way.
 */
export function formatDoorNames(doors: AccessDoor[]): string {
  const names = doors.map((door) => door.name)
  if (names.length <= 1) return names[0] ?? 'the door'
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** Confirmation heading after a fully successful unlock. */
export function unlockedTitle(unlockedCount: number): string {
  return unlockedCount > 1 ? 'Doors Unlocked' : 'Door Unlocked'
}

/** Title-cased "Door" / "Doors", for button labels. */
export function doorNounTitleCase(doors: AccessDoor[]): string {
  return doors.length > 1 ? 'Doors' : 'Door'
}

/** The primary CTA label, including its in-flight state. */
export function unlockButtonLabel(doors: AccessDoor[], pending: boolean): string {
  return pending ? UNLOCKING : `Unlock ${doorNounTitleCase(doors)}`
}

/**
 * Prompt under the heading, naming what a press will open. With a Bay PIN to follow, it says so
 * ("Slide to unlock the door, then you'll see your Bay PIN" — simulator-companion D77's door step).
 */
export function unlockPrompt(doors: AccessDoor[], verb: 'Tap' | 'Slide', thenBayPin = false): string {
  const prompt = doors.length > 1
    ? `${verb} to unlock ${formatDoorNames(doors)}`
    : `${verb} to unlock the door`
  return thenBayPin ? `${prompt},\nthen you'll see your ${BAY_PIN_LABEL}` : prompt
}

// ── Fixed strings ────────────────────────────────────────────────────────────
// Both surfaces show these verbatim. They were separately inlined in each page, which is the
// failure mode this file exists to prevent: identical UI drifting into two slightly different
// sentences because nobody noticed the other copy.

/** In-flight CTA label. */
export const UNLOCKING = 'Unlocking…'

/** Returns from the confirmation to the actionable state. Doors re-lock; the token stays good. */
export const UNLOCK_AGAIN = 'Unlock again'

/** Fallback when the unlock request itself failed and the server said nothing useful. */
export const UNLOCK_FAILED = 'Failed to unlock. Please try again.'

// ── The Bay PIN (simulator-companion D77) ────────────────────────────────────

/** The PIN step's heading, named for the bay ("Augusta PIN"); "Bay PIN" when the bay has no name. */
export function bayPinTitle(bayName: string | null | undefined): string {
  return bayName ? `${bayName} PIN` : BAY_PIN_LABEL
}

/** Under the PIN step's heading. */
export const BAY_PIN_PROMPT = 'Enter this PIN on the bay computer'

/** On every door step: someone may already have opened the door. */
export const SHOW_BAY_PIN = `Show ${BAY_PIN_LABEL}`

/** On the PIN step, back to a door that may need opening again. */
export const BACK_TO_DOOR = 'Back to the door'

/** The card holding a booking's door code and Bay PIN. */
export const ACCESS_CODES_TITLE = 'Access Codes'

/** The door's code in that card. */
export const DOOR_CODE_LABEL = 'Door code'

/**
 * Heading when the door set is not known yet (before the access window opens, no doors are
 * sent) or the club has none configured. An action, not a door name: "Entrance Door" read
 * as if a door were called that.
 */
export const DEFAULT_DOOR_NAME = 'Open Door'
